/**
 * Cloze Question Module
 * Handles rendering and validation of cloze/fill-in-the-blank questions
 * Extracted from main.js as part of Phase 2 modularization - Step 7
 */

import { EVENT_TYPES } from '../../core/event-types.js';
import { logEvent } from '../../core/event-logger.js';
import appState from '../../core/app-state.js';
import { i18nService } from '../../services/i18n-service.js';
import * as TranscriptModule from '../player/transcript.js';
import { generateOptionLabel } from './question-utils.js';
import { updateQuestionProgressBar, renderSummaryChip } from './question-manager.js';
import { renderCurrentQuestion } from './question-coordinator.js';
import { markClipCompleted, updateProgressUI } from '../../services/clip-coordinator.js';
import type { Clip, ClozeQuestion, ClozeBlank } from '../../types/clip.js';
import type { ClozeState } from '../../types/app-state.js';

/**
 * Update a blank in the sentence preview to show the correct answer
 * @param blank - The blank object
 * @param blankIndex - Index of the blank in the sorted blanks array
 */
function updateBlankWithAnswer(blank: ClozeBlank, blankIndex: number): void {
  const sentencePreview = document.querySelector('.cloze-sentence-preview');
  if (!sentencePreview) return;
  
  // Find all blank spans
  const blankSpans = sentencePreview.querySelectorAll('.cloze-blank');
  const targetSpan = blankSpans[blankIndex] as HTMLElement;
  
  if (targetSpan) {
    // Update the blank to show the correct answer
    targetSpan.textContent = blank.correct;
    targetSpan.classList.remove('cloze-blank-active', 'cloze-blank-future');
    targetSpan.classList.add('cloze-blank-completed');
  }
}

/**
 * Render a cloze question with stepwise blank filling
 * @param clip - The current clip
 * @param _idx - The clip index (unused but kept for interface consistency)
 */
export function renderClozeQuestion(clip: Clip, _idx: number): void {
  const q = clip.questions[appState.currentQuestionIndex] as ClozeQuestion;
  const questionContainer = document.getElementById('questionContainer');
  if (!questionContainer) return;
  
  // Initialize cloze state if not already present
  if (!appState.clozeState) {
    appState.clozeState = {
      blankIndex: 0,
      completedBlanks: []
    };
  }
  
  const clozeState: ClozeState = appState.clozeState;
  let blanks: ClozeBlank[] = q.blanks || [];
  
  // Get source text
  let sourceText = '';
  if (q.source === 'sentence') {
    const sentenceIdx = q.sentenceIndex ?? -1;
    if (Array.isArray(clip.sentences) && sentenceIdx >= 0 && sentenceIdx < clip.sentences.length) {
      const sentence = clip.sentences[sentenceIdx];
      if (sentence) {
        sourceText = sentence.text || '';
      }
    }
  } else if (q.source === 'custom' || q.source === 'script') {
    sourceText = q.text || '';
  }
  
  // Robust fallback: if q.text exists and sourceText is still empty, use q.text
  if (!sourceText && q.text) {
    sourceText = q.text;
  }
  
  // Backward compatibility: If blanks array is empty but old format fields exist, create a blank
  if (blanks.length === 0 && q.options && q.answerIndex !== undefined && q.prompt) {
    const blankMarker = '______';
    const blankIndex = q.prompt.indexOf(blankMarker);
    
    if (blankIndex !== -1 && q.options[q.answerIndex]) {
      // Create a blank from old format
      blanks = [{
        start: blankIndex,
        end: blankIndex + blankMarker.length,
        mode: 'mcq',
        options: q.options,
        correct: q.options[q.answerIndex] as string
      }];
      
      // Use prompt as source text if not already set
      if (!sourceText) {
        sourceText = q.prompt;
      }
    }
  }
  
  // Clear the question container
  questionContainer.innerHTML = '';
  
  const questionDiv = document.createElement('div');
  questionDiv.className = 'question-block mt-1';
  questionDiv.setAttribute('role', 'group');
  questionDiv.setAttribute('aria-labelledby', `question-prompt-${clip.id}-${appState.currentQuestionIndex}`);
  
  // Clear the central feedback region
  const feedbackRegion = document.getElementById('feedback-region');
  if (feedbackRegion) {
    feedbackRegion.innerHTML = '';
  }
  
  // Add question prompt - only if it's different from the sentence itself
  // For cloze questions, prompt often duplicates the sentence with blanks
  // Only show prompt if it's a true instruction/question, not the sentence
  const shouldShowPrompt = q.prompt && 
    q.prompt !== q.text && 
    q.prompt !== sourceText &&
    !q.prompt.includes('______'); // Don't show if prompt contains blanks
  
  if (shouldShowPrompt) {
    const questionText = document.createElement('p');
    questionText.textContent = q.prompt;
    questionText.className = 'mb-3 text-lg font-semibold text-gray-800 text-center';
    questionText.id = `question-prompt-${clip.id}-${appState.currentQuestionIndex}`;
    questionDiv.appendChild(questionText);
  }
  
  // Add progress indicator
  if (blanks.length > 1) {
    const progressText = document.createElement('p');
    progressText.textContent = i18nService.formatUIString('nextBlank', { current: clozeState.blankIndex + 1, total: blanks.length });
    progressText.className = 'text-sm text-gray-600 text-center mb-2';
    progressText.setAttribute('aria-live', 'polite');
    questionDiv.appendChild(progressText);
  }
  
  // Build sentence preview with blanks
  const sentencePreview = document.createElement('div');
  sentencePreview.className = 'cloze-sentence-preview mb-4 p-3 bg-gray-50 rounded-lg text-center text-lg font-semibold text-gray-800';
  sentencePreview.setAttribute('aria-label', 'Sentence with blanks');
  
  // Sort blanks by position for rendering
  const sortedBlanks = [...blanks].sort((a, b) => a.start - b.start);
  
  let lastIndex = 0;
  let previewHTML = '';
  
  sortedBlanks.forEach((blank, blankIdx) => {
    // Add text before blank
    previewHTML += escapeHtml(sourceText.substring(lastIndex, blank.start));
    
    // Add blank placeholder
    const isCompleted = clozeState.completedBlanks.includes(blankIdx);
    const isCurrent = blankIdx === clozeState.blankIndex;
    
    if (isCompleted) {
      // Show completed blank with correct answer
      previewHTML += `<span class="cloze-blank cloze-blank-completed">${escapeHtml(blank.correct)}</span>`;
    } else if (isCurrent) {
      // Show current blank as pill/underline
      previewHTML += `<span class="cloze-blank cloze-blank-active">______</span>`;
    } else {
      // Show future blank
      previewHTML += `<span class="cloze-blank cloze-blank-future">______</span>`;
    }
    
    lastIndex = blank.end;
  });
  
  // Add remaining text after last blank
  previewHTML += escapeHtml(sourceText.substring(lastIndex));
  
  sentencePreview.innerHTML = previewHTML;
  questionDiv.appendChild(sentencePreview);
  
  // Current blank to fill
  if (clozeState.blankIndex < blanks.length) {
    const currentBlank = blanks[clozeState.blankIndex];
    if (!currentBlank) return; // Guard against undefined
    
    // Create input area based on blank mode
    if (currentBlank.mode === 'mcq') {
      // MCQ mode: show option buttons
      const optionsContainer = document.createElement('div');
      optionsContainer.className = 'cloze-options-container';
      
      currentBlank.options.forEach((option, optIdx) => {
        const btn = document.createElement('button');
        btn.className = 'answer-card';
        btn.setAttribute('role', 'option');
        btn.setAttribute('aria-selected', 'false');
        
        // Generate the option label (A, B, C, etc.)
        const label = generateOptionLabel(optIdx);
        
        // Create badge element
        const badge = document.createElement('span');
        badge.className = 'answer-badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.textContent = label;
        
        // Create visually hidden span for screen readers
        const srLabel = document.createElement('span');
        srLabel.className = 'sr-only';
        srLabel.textContent = `${label}. `;
        
        // Create text container
        const textSpan = document.createElement('span');
        textSpan.className = 'answer-text';
        textSpan.textContent = option;
        
        // Add elements to button
        btn.appendChild(badge);
        btn.appendChild(srLabel);
        btn.appendChild(textSpan);
        
        // Add click handler
        btn.addEventListener('click', () => {
          handleClozeAnswer(clip, q, option, currentBlank, btn, optionsContainer, questionDiv, clozeState);
        });
        
        optionsContainer.appendChild(btn);
      });
      
      questionDiv.appendChild(optionsContainer);
      
    } else {
      // Free text mode: show input field and Check button
      const inputContainer = document.createElement('div');
      inputContainer.className = 'cloze-input-container mt-3';
      
      const inputLabel = document.createElement('label');
      inputLabel.textContent = 'Your answer:';
      inputLabel.className = 'block text-sm font-medium text-gray-700 mb-1';
      inputLabel.setAttribute('for', `cloze-input-${clip.id}-${appState.currentQuestionIndex}`);
      inputContainer.appendChild(inputLabel);
      
      const inputWrapper = document.createElement('div');
      inputWrapper.className = 'flex gap-2';
      
      const input = document.createElement('input');
      input.type = 'text';
      input.id = `cloze-input-${clip.id}-${appState.currentQuestionIndex}`;
      input.className = 'cloze-text-input flex-1 px-3 py-2 border border-gray-300 rounded-lg';
      input.maxLength = 20;
      input.placeholder = i18nService.getString('typeYourAnswer') || 'Type your answer...';
      input.setAttribute('aria-label', 'Answer input');
      
      const checkBtn = document.createElement('button');
      checkBtn.textContent = i18nService.getString('checkAnswer') || 'Check';
      checkBtn.className = 'cloze-check-btn px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium';
      checkBtn.setAttribute('aria-label', 'Check answer');
      
      // Handle Enter key in input
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          checkBtn.click();
        }
      });
      
      // Check button handler
      checkBtn.addEventListener('click', () => {
        const userAnswer = input.value;
        handleClozeAnswer(clip, q, userAnswer, currentBlank, checkBtn, inputContainer, questionDiv, clozeState);
      });
      
      inputWrapper.appendChild(input);
      inputWrapper.appendChild(checkBtn);
      inputContainer.appendChild(inputWrapper);
      questionDiv.appendChild(inputContainer);
      
      // Focus the input
      setTimeout(() => input.focus(), 100);
    }
  }
  
  // Create feedback container
  const questionFeedback = document.createElement('div');
  questionFeedback.className = 'question-feedback';
  questionFeedback.setAttribute('aria-live', 'polite');
  questionDiv.appendChild(questionFeedback);
  
  questionContainer.appendChild(questionDiv);
  
  // Log cloze question render
  logEvent(EVENT_TYPES['QUESTION_RENDER']!, {
    clipId: clip.id,
    questionIndex: appState.currentQuestionIndex,
    questionText: q.prompt,
    questionType: 'cloze',
    blanksCount: blanks.length,
    blankIndex: clozeState.blankIndex
  });
}

/**
 * Handle cloze answer submission
 * @param clip - The current clip
 * @param question - The question object
 * @param userAnswer - User's answer
 * @param blank - The blank object
 * @param buttonOrInput - The button or input element
 * @param container - The container element
 * @param questionDiv - The question div
 * @param clozeState - The cloze state object
 */
function handleClozeAnswer(
  clip: Clip, 
  question: ClozeQuestion, 
  userAnswer: string, 
  blank: ClozeBlank, 
  buttonOrInput: HTMLElement, 
  container: HTMLElement, 
  questionDiv: HTMLElement, 
  clozeState: ClozeState
): void {
  // Normalize both answers for comparison
  const normalizedUser = normalizeForCheck(userAnswer);
  const normalizedCorrect = normalizeForCheck(blank.correct);
  
  const isCorrect = normalizedUser === normalizedCorrect;
  
  // Initialize attempt tracking if not present
  if (!appState.questionAttempts) {
    appState.questionAttempts = {};
  }
  
  // Track attempts per blank within the question
  const blankKey = `${clip.id}-${appState.currentQuestionIndex}-blank${clozeState.blankIndex}`;
  const attempts = (appState.questionAttempts[blankKey] || 0) + 1;
  appState.questionAttempts[blankKey] = attempts;
  
  const MAX_ATTEMPTS = 2; // Allow 2 tries before revealing
  
  // Find feedback element
  let feedbackEl = questionDiv.querySelector('.question-feedback');
  if (!feedbackEl) {
    feedbackEl = document.createElement('div');
    feedbackEl.className = 'question-feedback';
    feedbackEl.setAttribute('aria-live', 'polite');
    questionDiv.appendChild(feedbackEl);
  }
  
  if (isCorrect) {
    // Correct answer - mark blank as completed and advance
    const currentBlankIndex = clozeState.blankIndex;
    clozeState.completedBlanks.push(currentBlankIndex);
    
    // Update the blank display to show the correct answer
    updateBlankWithAnswer(blank, currentBlankIndex);
    
    clozeState.blankIndex++;
    
    // For MCQ: mark button as correct
    if (blank.mode === 'mcq' && buttonOrInput instanceof HTMLButtonElement) {
      buttonOrInput.classList.add('correct');
      buttonOrInput.disabled = true;
    }
    
    // Safe access to blanks array
    const blanksLength = question.blanks?.length || 0;
    
    // Announce success
    if (clozeState.blankIndex < blanksLength) {
      TranscriptModule.announceToScreenReader(`${i18nService.getString('correct') || 'Correct!'} Moving to blank ${clozeState.blankIndex + 1} of ${blanksLength}`);
      feedbackEl.textContent = '';
    } else {
      TranscriptModule.announceToScreenReader('All blanks correct! Question complete.');
      // Don't show "All blanks completed correctly!" message - keep feedback area clean
      feedbackEl.textContent = '';
    }
    
    // Log correct blank
    logEvent(EVENT_TYPES['ANSWER_SELECT']!, {
      clipId: clip.id,
      questionIndex: appState.currentQuestionIndex,
      questionType: 'cloze',
      blankIndex: clozeState.blankIndex - 1,
      isCorrect: true,
      attempts: attempts,
      subStep: clozeState.blankIndex,
      subStepCount: blanksLength
    });
    
    // Check if all blanks are complete
    if (clozeState.blankIndex >= blanksLength) {
      // All blanks complete! Mark question as correct (only if all blanks were correct on first or second try)
      if (!appState.clipAnswers.has(clip.id)) {
        appState.clipAnswers.set(clip.id, []);
      }
      const answers = appState.clipAnswers.get(clip.id);
      if (answers) {
        answers[appState.currentQuestionIndex] = true;
      }
      
      // Clear cloze state
      appState.clozeState = undefined;
      
      // Increment question index
      appState.currentQuestionIndex++;
      
      // Add Continue or Finish button
      if (appState.currentQuestionIndex < clip.questions.length) {
        // Not the last question - add Continue button
        const continueBtn = document.createElement('button');
        continueBtn.id = 'nextQuestionInClip';
        continueBtn.textContent = i18nService.getString('continue') || 'Continue';
        continueBtn.className = 'inline-continue-btn';
        continueBtn.addEventListener('click', () => {
          renderCurrentQuestion(clip, appState.currentClipIdx);
          updateQuestionProgressBar();
          logEvent(EVENT_TYPES['QUESTION_CONTINUE']!, { clipId: clip.id, questionIndex: appState.currentQuestionIndex - 1 });
        });
        questionDiv.appendChild(continueBtn);
        continueBtn.focus();
      } else {
        // Last question - mark clip as completed and show summary chip inline below the question
        // instead of replacing the question with the summary
        markClipCompleted(clip.id);
        updateProgressUI();
        updateQuestionProgressBar();
        
        // Create a container for the summary that will be appended to questionDiv
        const summaryContainer = document.createElement('div');
        summaryContainer.className = 'inline-summary-container mt-4';
        questionDiv.appendChild(summaryContainer);
        
        // Render the summary chip into this container
        renderSummaryChip(clip, summaryContainer);
      }
    } else {
      // More blanks to go - re-render with next blank
      setTimeout(() => {
        renderClozeQuestion(clip, appState.clips.findIndex(c => c.id === clip.id));
      }, 100);
    }
    
  } else if (attempts < MAX_ATTEMPTS) {
    // Incorrect answer, but retries remaining: flash red, DON'T reveal
    
    // For MCQ: flash red and allow retry (don't persist incorrect state)
    if (blank.mode === 'mcq' && buttonOrInput instanceof HTMLButtonElement) {
      buttonOrInput.classList.add('incorrect-flash');
      TranscriptModule.announceToScreenReader('Incorrect. Please try another option.');
      
      // Remove flash after animation
      setTimeout(() => {
        buttonOrInput.classList.remove('incorrect-flash');
      }, 500);
    } else {
      // For free text: clear input and refocus
      const input = container.querySelector('input');
      if (input) {
        input.value = '';
        input.focus();
        TranscriptModule.announceToScreenReader('Incorrect. Please try again.');
      }
    }
    
    // Log incorrect answer
    logEvent(EVENT_TYPES['ANSWER_SELECT']!, {
      clipId: clip.id,
      questionIndex: appState.currentQuestionIndex,
      questionType: 'cloze',
      blankIndex: clozeState.blankIndex,
      isCorrect: false,
      attempts: attempts,
      subStep: clozeState.blankIndex + 1,
      subStepCount: question.blanks?.length || 0
    });
    
  } else {
    // Max attempts reached: reveal correct answer and auto-advance
    const currentBlankIndex = clozeState.blankIndex;
    clozeState.completedBlanks.push(currentBlankIndex);
    
    // Update the blank display to show the correct answer
    updateBlankWithAnswer(blank, currentBlankIndex);
    
    clozeState.blankIndex++;
    
    // For MCQ: mark buttons - selected as incorrect, correct answer as correct
    if (blank.mode === 'mcq' && container.classList.contains('cloze-options-container')) {
      const buttons = container.querySelectorAll('button');
      buttons.forEach((btn) => {
        btn.disabled = true;
        const textSpan = btn.querySelector('.answer-text');
        const btnText = (textSpan ? textSpan.textContent : btn.textContent)?.trim() || '';
        
        if (btnText === userAnswer.trim()) {
          // The button they clicked - mark as incorrect
          btn.classList.add('incorrect', 'locked');
        } else if (btnText === blank.correct.trim()) {
          // The correct answer - highlight it
          btn.classList.add('correct', 'locked');
        } else {
          // Other options - dim them
          btn.classList.add('dim-unselected', 'locked');
        }
      });
    }
    
    // Show feedback that correct answer was revealed
    TranscriptModule.announceToScreenReader(`The correct answer was: ${blank.correct}`);
    
    // Safe access to blanks array
    const blanksLength = question.blanks?.length || 0;
    
    // Log incorrect answer
    logEvent(EVENT_TYPES['ANSWER_SELECT']!, {
      clipId: clip.id,
      questionIndex: appState.currentQuestionIndex,
      questionType: 'cloze',
      blankIndex: currentBlankIndex,
      isCorrect: false,
      attempts: attempts,
      revealed: true,
      subStep: clozeState.blankIndex,
      subStepCount: blanksLength
    });
    
    // Check if all blanks are complete
    if (clozeState.blankIndex >= blanksLength) {
      // All blanks complete! Mark question as incorrect (at least one blank failed)
      if (!appState.clipAnswers.has(clip.id)) {
        appState.clipAnswers.set(clip.id, []);
      }
      const answers = appState.clipAnswers.get(clip.id);
      if (answers) {
        answers[appState.currentQuestionIndex] = false;
      }
      
      // Clear cloze state
      appState.clozeState = undefined;
      
      // Increment question index
      appState.currentQuestionIndex++;
      
      // Add Continue or Finish button
      if (appState.currentQuestionIndex < clip.questions.length) {
        // Not the last question - add Continue button
        const continueBtn = document.createElement('button');
        continueBtn.id = 'nextQuestionInClip';
        continueBtn.textContent = i18nService.getString('continue') || 'Continue';
        continueBtn.className = 'inline-continue-btn';
        continueBtn.addEventListener('click', () => {
          renderCurrentQuestion(clip, appState.currentClipIdx);
          updateQuestionProgressBar();
          logEvent(EVENT_TYPES['QUESTION_CONTINUE']!, { clipId: clip.id, questionIndex: appState.currentQuestionIndex - 1 });
        });
        questionDiv.appendChild(continueBtn);
        continueBtn.focus();
      } else {
        // Last question - mark clip as completed and show summary chip inline below the question
        markClipCompleted(clip.id);
        updateProgressUI();
        updateQuestionProgressBar();
        
        // Create a container for the summary that will be appended to questionDiv
        const summaryContainer = document.createElement('div');
        summaryContainer.className = 'inline-summary-container mt-4';
        questionDiv.appendChild(summaryContainer);
        
        // Render the summary chip into this container
        renderSummaryChip(clip, summaryContainer);
      }
    } else {
      // More blanks to go - re-render with next blank after a short delay
      setTimeout(() => {
        renderClozeQuestion(clip, appState.clips.findIndex(c => c.id === clip.id));
      }, 1500); // Longer delay to show the revealed answer
    }
  }
}

/**
 * Helper to normalize text for Turkish comparison (case-insensitive, trimmed, collapsed spaces)
 * @param str - The string to normalize
 * @returns Normalized string
 */
function normalizeForCheck(str: string): string {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .toLocaleLowerCase('tr-TR')
    .replace(/\s+/g, ' ')  // Collapse multiple spaces to single space
    .replace(/[.,!?;:'"¡¿]/g, '');  // Remove basic punctuation for minor differences
}

/**
 * Helper to escape HTML for safe rendering
 * @param text - The text to escape
 * @returns Escaped HTML
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
